import { useEffect, useRef, useState } from "react";
import { useT, type MessageKey } from "../../i18n/index.js";

/**
 * F2 checklist + F-chk-lines (CEO 2026-09-19) — bloco compartilhado dos
 * portais (técnico + responsável do chantier), agora em LINHAS finas:
 *
 *  - fotos em SEQUÊNCIA GUIADA (frente → lateral frontal dir. → lateral
 *    frontal esq. → lateral traseira esq. → lateral traseira dir. → capô →
 *    traseira → painel), uma por vez, com progresso X/N; re-tirar tocando na
 *    miniatura; condicionais pára-brisa trincado / ciel com defeito entram no
 *    fim da sequência.
 *  - toggles pára-brisa OK/trincado e ciel de toit OK/defeito.
 *  - assinatura do cliente desenhada na tela (canvas → PNG) + nome.
 *
 * Componente CONTROLADO: o pai guarda o rascunho e faz o save; o upload de
 * foto é imediato (callback do pai → tRPC do portal correspondente).
 */

export type ChecklistDraft = {
  mileage: string;
  pareBrise: "" | "ok" | "nok";
  ciel: "" | "ok" | "nok";
  /** PNG data-URL desenhado NESTA sessão (null = sem novo desenho). */
  signature: string | null;
  signedName: string;
};

export const EMPTY_CHECKLIST_DRAFT: ChecklistDraft = {
  mileage: "",
  pareBrise: "",
  ciel: "",
  signature: null,
  signedName: "",
};

export type PhotoSlotKind =
  | "front"
  | "front_right"
  | "front_left"
  | "rear_left"
  | "rear_right"
  | "hood"
  | "rear"
  | "dashboard"
  | "hood_liner"
  | "door_trim_1"
  | "door_trim_2"
  | "door_trim_3"
  | "door_trim_4"
  | "door_trim_5"
  | "rear_light_1"
  | "rear_light_2"
  | "rear_light_3"
  | "windshield"
  | "headliner"
  | "headliner_front"
  | "headliner_rear"
  | "free"
  | "angle_1"
  | "angle_2"
  | "angle_3"
  | "angle_4"
  | "roof";

/**
 * Sequência guiada da RECEPÇÃO — as mesmas 5 poses das etapas do montador
 * (CEO 2026-09-22: "deixe 5 fotos também para a recepção do PDR e gerente").
 * Vale para todo mundo: técnico PDR, montador e gerente do chantier.
 * ORDEM fixada pelo CEO em 2026-09-23 ("IMPORTANTE que seja nesta
 * sequência"): forro do teto frente → forro do teto trás → painel com a
 * chave ligada → forro do capô → traseira. Espelha `MONTAGE_PHOTOS` da API.
 */
export const REQUIRED_PHOTO_SLOTS: PhotoSlotKind[] = [
  "headliner_front",
  "headliner_rear",
  "dashboard",
  "hood_liner",
  "rear",
];

/**
 * Conjuntos ANTIGOS aceitos como completos: os 8 pontos de 19/09 e os 6 do
 * F2 original. Sem isto todo carro já recebido apareceria "incompleto" — e a
 * trava do reparo passaria a cobrar fotos de carros que já estão no pátio.
 */
export const LEGACY_RECEPTION_SETS: PhotoSlotKind[][] = [
  ["front", "front_right", "front_left", "rear_left", "rear_right", "hood", "rear", "dashboard"],
  ["angle_1", "angle_2", "angle_3", "angle_4", "roof", "dashboard"],
];

/**
 * Sequência guiada do MONTADOR (desmontagem e fim do trabalho).
 * CEO 2026-09-22: das 11 poses de 19/09 para 4 — forro do teto, frente,
 * traseira, painel (chave ligada). Carro já fotografado com as 11 antigas
 * continua completo (LEGACY_MONTAGE_SLOTS).
 */
export const MONTAGE_PHOTO_SLOTS: PhotoSlotKind[] = [
  "headliner_front",
  "headliner_rear",
  "dashboard",
  "hood_liner",
  "rear",
];

export const LEGACY_MONTAGE_SLOTS: PhotoSlotKind[] = [
  "dashboard",
  "headliner",
  "hood_liner",
  "door_trim_1",
  "door_trim_2",
  "door_trim_3",
  "door_trim_4",
  "door_trim_5",
  "rear_light_1",
  "rear_light_2",
  "rear_light_3",
];

/**
 * Recepção do MONTADOR: convergiu com a de todo mundo em 2026-09-22. Mantido
 * como nome próprio porque os dois papéis já divergiram antes e podem
 * divergir de novo — trocar aqui não mexe no resto.
 */
export const MONTADOR_RECEPTION_SLOTS: PhotoSlotKind[] = ["rear", "dashboard"];

/**
 * F-recepcao-2-fotos (CEO 2026-10-04): recepção feita por TÉCNICO (portal do
 * técnico, PDR ou montador) = só traseira + painel com a chave ligada. O
 * gerente do chantier continua com as 5 (REQUIRED_PHOTO_SLOTS). Espelha
 * `TECH_RECEPTION_PHOTOS` da API.
 */
export const TECH_RECEPTION_SLOTS: PhotoSlotKind[] = MONTADOR_RECEPTION_SLOTS;

/**
 * O que ainda falta no check-list, item a item.
 *
 * F-chk-falta (GP-108-AE, CEO 2026-09-21) — o técnico assinou, apertou
 * "Salvar check-list" seis vezes e concluiu que o app tinha travado: faltavam
 * 6 das 8 fotos e a tela não dizia isso em lugar nenhum. `checklistComplete`
 * delega para cá justamente para o que a tela cobra e o que o servidor exige
 * nunca discordarem.
 */
export function checklistMissing(opts: {
  pareBriseOk: boolean | null;
  cielOk: boolean | null;
  signed: boolean;
  photoKinds: Set<string>;
  /** Recepção feita pelo montador exige o conjunto curto. */
  montador?: boolean;
  /** Recepção feita por técnico (portal do técnico): 2 fotos. */
  tecnico?: boolean;
  /** CEO 2026-09-22: assinatura só na recepção feita pelo GERENTE. */
  requireSignature?: boolean;
}): { photos: PhotoSlotKind[]; signature: boolean; pareBrise: boolean; ciel: boolean } {
  const legacyOk = LEGACY_RECEPTION_SETS.some((set) => set.every((k) => opts.photoKinds.has(k)));
  const exigidas = opts.tecnico || opts.montador ? TECH_RECEPTION_SLOTS : REQUIRED_PHOTO_SLOTS;
  const photos = (legacyOk ? [] : exigidas).filter((k) => !opts.photoKinds.has(k));
  if (opts.pareBriseOk === false && !opts.photoKinds.has("windshield")) photos.push("windshield");
  if (opts.cielOk === false && !opts.photoKinds.has("headliner")) photos.push("headliner");
  return {
    photos,
    signature: opts.requireSignature === false ? false : !opts.signed,
    pareBrise: opts.pareBriseOk == null,
    ciel: opts.cielOk == null,
  };
}

/** true quando todos os pontos F2 estão completos NO SERVIDOR. */
export function checklistComplete(opts: {
  mileageKm: number | null;
  pareBriseOk: boolean | null;
  cielOk: boolean | null;
  signed: boolean;
  photoKinds: Set<string>;
  montador?: boolean;
  tecnico?: boolean;
  requireSignature?: boolean;
}): boolean {
  // CEO 2026-09-19: km não é mais digitado (a foto do painel com a chave
  // ligada mostra o km) — não conta para a completude.
  void opts.mileageKm;
  const falta = checklistMissing(opts);
  return falta.photos.length === 0 && !falta.signature && !falta.pareBrise && !falta.ciel;
}

/**
 * CEO 2026-10-15 (Android): sem `capture`, o Chrome novo do Android abre SÓ o
 * seletor de fotos (galeria), sem câmera; com `capture`, o iPhone abre SÓ a
 * câmera (F-foto-galeria, 2026-09-25). Dois botões explícitos atendem os
 * dois: "Câmera" = input com `capture`, "Galeria" = input sem `capture`.
 * O input vai ao DOM: no Safari, um input solto (fora do documento) pode ser
 * descartado antes do `change` e o upload nunca acontece.
 */
export type PhotoSource = "camera" | "gallery";

export function openPhotoPicker(source: PhotoSource, onFile: (f: File) => void): void {
  const inp = document.createElement("input");
  inp.type = "file";
  inp.accept = "image/*";
  if (source === "camera") inp.setAttribute("capture", "environment");
  inp.style.display = "none";
  inp.onchange = () => {
    const f = inp.files?.[0];
    inp.remove();
    if (f) onFile(f);
  };
  document.body.appendChild(inp);
  inp.click();
}

function PhotoSourceButtons({
  onPick,
  dark,
}: {
  onPick: (source: PhotoSource) => void;
  dark: boolean;
}) {
  const t = useT();
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        onClick={() => onPick("camera")}
        className="whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-bold"
        style={{ background: "#CA8A04", color: "#fff" }}
        data-testid="photo-source-camera"
      >
        📷 {t("techPortal.chkPhotoCameraBtn")}
      </button>
      <button
        type="button"
        onClick={() => onPick("gallery")}
        className="whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-bold"
        style={
          dark
            ? { borderColor: "#CA8A04", color: "#FACC15", background: "transparent" }
            : { borderColor: "#CA8A04", color: "#854D0E", background: "#fff" }
        }
        data-testid="photo-source-gallery"
      >
        🖼️ {t("techPortal.chkPhotoGalleryBtn")}
      </button>
    </div>
  );
}

/** Escolha Câmera/Galeria para refazer uma foto ou tirar a foto livre. */
function PhotoSourceChooser({
  kind,
  dark,
  onPick,
  onCancel,
}: {
  kind: PhotoSlotKind;
  dark: boolean;
  onPick: (source: PhotoSource) => void;
  onCancel: () => void;
}) {
  const t = useT();
  return (
    <div
      className="space-y-1.5 rounded-md border p-2"
      style={
        dark
          ? { borderColor: "#CA8A04", background: "#0a0a0a" }
          : { borderColor: "#CA8A04", background: "#FEF9C3" }
      }
      data-testid="photo-source-chooser"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs" style={dark ? { color: "#d4d4d4" } : { color: "#854D0E" }}>
          {t("techPortal.chkPhotoSourcePre")} <strong>{t(SLOT_LABEL_KEYS[kind])}</strong>
        </span>
        <button
          type="button"
          onClick={onCancel}
          title={t("techPortal.chkPhotoCancelBtn")}
          aria-label={t("techPortal.chkPhotoCancelBtn")}
          className="shrink-0 rounded-md px-1.5 text-xs leading-none"
          style={dark ? { color: "#a3a3a3" } : { color: "#854D0E" }}
        >
          ✕
        </button>
      </div>
      <PhotoSourceButtons dark={dark} onPick={onPick} />
    </div>
  );
}

/** Rótulo traduzido de um ponto de foto (usado também fora deste arquivo). */
export function photoSlotLabelKey(kind: PhotoSlotKind): MessageKey {
  return SLOT_LABEL_KEYS[kind];
}

const SLOT_LABEL_KEYS: Record<PhotoSlotKind, MessageKey> = {
  front: "techPortal.chkPhotoFront",
  front_right: "techPortal.chkPhotoFrontRight",
  front_left: "techPortal.chkPhotoFrontLeft",
  rear_left: "techPortal.chkPhotoRearLeft",
  rear_right: "techPortal.chkPhotoRearRight",
  hood: "techPortal.chkPhotoHood",
  rear: "techPortal.chkPhotoRear",
  dashboard: "techPortal.chkPhotoDashboard",
  hood_liner: "techPortal.chkPhotoHoodLiner",
  door_trim_1: "techPortal.chkPhotoDoorTrim1",
  door_trim_2: "techPortal.chkPhotoDoorTrim2",
  door_trim_3: "techPortal.chkPhotoDoorTrim3",
  door_trim_4: "techPortal.chkPhotoDoorTrim4",
  door_trim_5: "techPortal.chkPhotoDoorTrim5",
  rear_light_1: "techPortal.chkPhotoRearLight1",
  rear_light_2: "techPortal.chkPhotoRearLight2",
  rear_light_3: "techPortal.chkPhotoRearLight3",
  windshield: "techPortal.chkPhotoWindshield",
  headliner: "techPortal.chkPhotoHeadliner",
  headliner_front: "techPortal.chkPhotoHeadlinerFront",
  headliner_rear: "techPortal.chkPhotoHeadlinerRear",
  free: "techPortal.chkPhotoFree",
  angle_1: "techPortal.chkPhotoAngle1",
  angle_2: "techPortal.chkPhotoAngle2",
  angle_3: "techPortal.chkPhotoAngle3",
  angle_4: "techPortal.chkPhotoAngle4",
  roof: "techPortal.chkPhotoRoof",
};

export function ChecklistExtras({
  value,
  onChange,
  photos,
  onUploadPhoto,
  uploadingKind,
  alreadySigned,
  showMileage = true,
  montador = false,
  tecnico = false,
  showSignature = true,
  tone,
}: {
  value: ChecklistDraft;
  onChange: (v: ChecklistDraft) => void;
  photos: Array<{ kind: string; url: string }>;
  onUploadPhoto: (kind: PhotoSlotKind, file: File) => void;
  uploadingKind: string | null;
  /** Assinatura já registrada no servidor (não precisa assinar de novo). */
  alreadySigned: boolean;
  showMileage?: boolean;
  /** Recepção do MONTADOR: sequência curta (traseira, painel). */
  montador?: boolean;
  /** Recepção feita por técnico (portal do técnico): traseira + painel. */
  tecnico?: boolean;
  /** Técnico não colhe assinatura (só o gerente do chantier). */
  showSignature?: boolean;
  /** "light" = card âmbar do técnico; "dark" = portal do chantier. */
  tone: "light" | "dark";
}) {
  const t = useT();
  const dark = tone === "dark";
  const lineCls = dark
    ? "rounded-md border border-neutral-800 bg-neutral-950/60 px-2.5 py-2"
    : "rounded-md border px-2.5 py-2";
  const lineStyle = dark ? undefined : { borderColor: "#CA8A04", background: "#fff" };
  const inputCls = dark
    ? "rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm outline-none focus:border-lime"
    : "rounded-md border px-3 py-1.5 text-sm";
  const inputStyle = dark
    ? undefined
    : { borderColor: "#CA8A04", background: "#fff", color: "#1F2937" };
  const labelCls = dark ? "text-sm text-neutral-300" : "text-sm font-semibold";
  const labelStyle = dark ? undefined : { color: "#854D0E" };

  const byKind = new Map(photos.map((p) => [p.kind, p.url]));
  const legacyOk = LEGACY_RECEPTION_SETS.some((set) => set.every((k) => byKind.has(k)));
  // Sequência: obrigatórias + condicionais quando "trincado"/"defeito".
  const sequence: PhotoSlotKind[] = [
    ...(legacyOk ? [] : tecnico || montador ? TECH_RECEPTION_SLOTS : REQUIRED_PHOTO_SLOTS),
    ...(value.pareBrise === "nok" ? (["windshield"] as const) : []),
    ...(value.ciel === "nok" ? (["headliner"] as const) : []),
  ];
  const nextKind = sequence.find((k) => !byKind.has(k)) ?? null;
  const takenCount = sequence.filter((k) => byKind.has(k)).length;
  // Foto a refazer / foto livre: abre a escolha Câmera × Galeria.
  const [chooser, setChooser] = useState<PhotoSlotKind | null>(null);

  function pick(kind: PhotoSlotKind, source: PhotoSource) {
    setChooser(null);
    openPhotoPicker(source, (f) => onUploadPhoto(kind, f));
  }

  function toggleLine(label: string, field: "pareBrise" | "ciel", nokLabel: string) {
    const v = value[field];
    return (
      <div className={`${lineCls} flex items-center justify-between gap-2`} style={lineStyle}>
        <span className={labelCls} style={labelStyle}>
          {label}
        </span>
        <div className="flex gap-1.5">
          {(["ok", "nok"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange({ ...value, [field]: opt })}
              className={
                dark
                  ? `rounded-md px-3 py-1 text-xs font-bold border ${
                      v === opt
                        ? opt === "ok"
                          ? "bg-lime text-on-lime border-lime"
                          : "bg-amber-500/20 text-amber-200 border-amber-500"
                        : "border-neutral-700 text-neutral-300"
                    }`
                  : "rounded-md px-3 py-1 text-xs font-bold"
              }
              style={
                dark
                  ? undefined
                  : v === opt
                    ? { background: "#CA8A04", color: "#fff" }
                    : { background: "#fff", color: "#854D0E", border: "1px solid #CA8A04" }
              }
            >
              {opt === "ok" ? t("techPortal.chkOkBtn") : nokLabel}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {showMileage && (
        <div className={`${lineCls} flex items-center justify-between gap-2`} style={lineStyle}>
          <span className={labelCls} style={labelStyle}>
            {t("techPortal.chkMileageLabel")}
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={value.mileage}
            onChange={(e) => onChange({ ...value, mileage: e.target.value })}
            placeholder={t("techPortal.chkMileagePh")}
            className={`${inputCls} w-32 text-right`}
            style={inputStyle}
          />
        </div>
      )}
      {toggleLine(t("techPortal.chkPareBriseLabel"), "pareBrise", t("techPortal.chkCrackedBtn"))}
      {toggleLine(t("techPortal.chkCielLabel"), "ciel", t("techPortal.chkDefectBtn"))}

      {/* Linha das FOTOS — sequência guiada, uma pose por vez. */}
      <div className={`${lineCls} space-y-1.5`} style={lineStyle}>
        <div className="flex items-center justify-between gap-2">
          <span className={labelCls} style={labelStyle}>
            📷 {t("techPortal.chkPhotosTitle")} — {takenCount}/{sequence.length}
          </span>
          {nextKind == null && (
            <span
              className={dark ? "text-xs font-semibold text-lime" : "text-xs font-semibold"}
              style={dark ? undefined : { color: "#166534" }}
            >
              ✓ {t("techPortal.chkPhotosDone")}
            </span>
          )}
        </div>
        {nextKind != null && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className={dark ? "text-xs text-neutral-400" : "text-xs"} style={labelStyle}>
              {t("techPortal.chkNextPhotoPre")} <strong>{t(SLOT_LABEL_KEYS[nextKind])}</strong>
            </span>
            {uploadingKind != null ? (
              <span className="shrink-0 text-xs font-bold" style={{ color: "#CA8A04" }}>
                {t("techPortal.chkUploading")}
              </span>
            ) : (
              <PhotoSourceButtons dark={dark} onPick={(src) => pick(nextKind, src)} />
            )}
          </div>
        )}
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {sequence
              .filter((k) => byKind.has(k))
              .map((kind) => (
                <button
                  key={kind}
                  type="button"
                  title={t(SLOT_LABEL_KEYS[kind])}
                  disabled={uploadingKind != null}
                  onClick={() => setChooser(kind)}
                  className="relative h-11 w-11 overflow-hidden rounded border"
                  style={{
                    borderColor: "#65A30D",
                    outline: chooser === kind ? "2px solid #CA8A04" : undefined,
                  }}
                >
                  <img
                    src={byKind.get(kind)}
                    alt={t(SLOT_LABEL_KEYS[kind])}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            <button
              type="button"
              title={t("techPortal.chkPhotoFree")}
              disabled={uploadingKind != null}
              onClick={() => setChooser("free")}
              className="h-11 w-11 rounded border text-lg"
              style={
                dark
                  ? { borderColor: "#404040", color: "#a3a3a3" }
                  : { borderColor: "#CA8A04", background: "#fff", color: "#854D0E" }
              }
            >
              ＋
            </button>
          </div>
        )}
        {photos.length === 0 && chooser == null && (
          <button
            type="button"
            title={t("techPortal.chkPhotoFree")}
            disabled={uploadingKind != null}
            onClick={() => setChooser("free")}
            className="text-xs underline"
            style={dark ? { color: "#a3a3a3" } : { color: "#854D0E" }}
          >
            ＋ {t("techPortal.chkPhotoFree")}
          </button>
        )}
        {chooser != null && uploadingKind == null && (
          <PhotoSourceChooser
            kind={chooser}
            dark={dark}
            onPick={(src) => pick(chooser, src)}
            onCancel={() => setChooser(null)}
          />
        )}
      </div>

      {/* Linha da ASSINATURA — só no portal do gerente (CEO 2026-09-22). */}
      {showSignature && (
        <div className={`${lineCls} space-y-1.5`} style={lineStyle}>
          <p className={labelCls} style={labelStyle}>
            ✍️ {t("techPortal.chkSignTitle")}
          </p>
          {alreadySigned && !value.signature ? (
            <p
              className={
                dark
                  ? "rounded-md border border-lime/30 bg-lime/5 px-2.5 py-1 text-xs text-lime"
                  : "rounded-md px-2.5 py-1 text-xs font-semibold"
              }
              style={dark ? undefined : { background: "#DCFCE7", color: "#166534" }}
            >
              ✓ {t("techPortal.chkSignedBadge")}
            </p>
          ) : null}
          <SignaturePad
            dark={dark}
            value={value.signature}
            onChange={(sig) => onChange({ ...value, signature: sig })}
          />
          <input
            value={value.signedName}
            onChange={(e) => onChange({ ...value, signedName: e.target.value })}
            placeholder={t("techPortal.chkSignNamePh")}
            className={`${inputCls} w-full`}
            style={inputStyle}
          />
        </div>
      )}
    </div>
  );
}

/** Pad de assinatura: desenho por pointer events → PNG data-URL. */
function SignaturePad({
  value,
  onChange,
  dark,
}: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  dark: boolean;
}) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  useEffect(() => {
    // Limpeza externa (value=null) reseta o quadro.
    if (value == null && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setHasStrokes(false);
    }
  }, [value]);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * e.currentTarget.width,
      y: ((e.clientY - rect.top) / rect.height) * e.currentTarget.height,
    };
  }

  return (
    <div className="space-y-1">
      <canvas
        ref={canvasRef}
        width={600}
        height={200}
        className="w-full touch-none rounded-md border"
        style={{ background: "#fff", borderColor: dark ? "#404040" : "#CA8A04", height: 110 }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawingRef.current = true;
          const ctx = e.currentTarget.getContext("2d");
          if (!ctx) return;
          const p = pos(e);
          ctx.strokeStyle = "#1F2937";
          ctx.lineWidth = 3;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
        }}
        onPointerMove={(e) => {
          if (!drawingRef.current) return;
          const ctx = e.currentTarget.getContext("2d");
          if (!ctx) return;
          const p = pos(e);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
        }}
        onPointerUp={(e) => {
          if (!drawingRef.current) return;
          drawingRef.current = false;
          setHasStrokes(true);
          onChange(e.currentTarget.toDataURL("image/png"));
        }}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs" style={dark ? { color: "#737373" } : { color: "#854D0E" }}>
          {t("techPortal.chkSignHint")}
        </span>
        {hasStrokes && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className={
              dark
                ? "rounded-md border border-neutral-700 px-2.5 py-1 text-xs text-neutral-300"
                : "rounded-md px-2.5 py-1 text-xs font-semibold"
            }
            style={
              dark
                ? undefined
                : { background: "#fff", color: "#854D0E", border: "1px solid #CA8A04" }
            }
          >
            ✕ {t("techPortal.chkSignClear")}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * F-montador — linha de fotos GUIADAS genérica (mesma UX da recepção),
 * usada pelo checklist do montador (checkKind=montage) e reutilizável.
 */
export function GuidedPhotoLine({
  title,
  sequence,
  photos,
  onUploadPhoto,
  uploadingKind,
  tone,
  issueMode,
  onSetIssue,
}: {
  title: string;
  sequence: PhotoSlotKind[];
  photos: Array<{ kind: string; url: string; issue?: "defect" | "broken" | null }>;
  onUploadPhoto: (kind: PhotoSlotKind, file: File) => void;
  uploadingKind: string | null;
  tone: "light" | "dark";
  /**
   * F-montador-fases — o montador mexe com peças: no checklist de ANTES marca
   * o que já estava com defeito; no do FIM, o que quebrou no trabalho.
   */
  issueMode?: "defect" | "broken";
  onSetIssue?: (kind: PhotoSlotKind, issue: "defect" | "broken" | null, note: string) => void;
}) {
  const t = useT();
  const [openSlot, setOpenSlot] = useState<PhotoSlotKind | null>(null);
  const [issueNote, setIssueNote] = useState("");
  const dark = tone === "dark";
  const lineCls = dark
    ? "rounded-md border border-neutral-800 bg-neutral-950/60 px-2.5 py-2"
    : "rounded-md border px-2.5 py-2";
  const lineStyle = dark ? undefined : { borderColor: "#CA8A04", background: "#fff" };
  const labelCls = dark ? "text-sm text-neutral-300" : "text-sm font-semibold";
  const labelStyle = dark ? undefined : { color: "#854D0E" };
  const byKind = new Map(photos.map((p) => [p.kind, p.url]));
  const issueByKind = new Map(photos.map((p) => [p.kind, p.issue ?? null]));
  const flagged = sequence.filter((k) => issueByKind.get(k) != null);
  const nextKind = sequence.find((k) => !byKind.has(k)) ?? null;
  const takenCount = sequence.filter((k) => byKind.has(k)).length;
  // Foto a refazer: abre a escolha Câmera × Galeria.
  const [chooser, setChooser] = useState<PhotoSlotKind | null>(null);

  function pick(kind: PhotoSlotKind, source: PhotoSource) {
    setChooser(null);
    openPhotoPicker(source, (f) => onUploadPhoto(kind, f));
  }

  return (
    <div className={`${lineCls} space-y-1.5`} style={lineStyle}>
      <div className="flex items-center justify-between gap-2">
        <span className={labelCls} style={labelStyle}>
          📷 {title} — {takenCount}/{sequence.length}
        </span>
        {nextKind == null && (
          <span
            className={dark ? "text-xs font-semibold text-lime" : "text-xs font-semibold"}
            style={dark ? undefined : { color: "#166534" }}
          >
            ✓ {t("techPortal.chkPhotosDone")}
          </span>
        )}
      </div>
      {nextKind != null && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={dark ? "text-xs text-neutral-400" : "text-xs"} style={labelStyle}>
            {t("techPortal.chkNextPhotoPre")} <strong>{t(SLOT_LABEL_KEYS[nextKind])}</strong>
          </span>
          {uploadingKind != null ? (
            <span className="shrink-0 text-xs font-bold" style={{ color: "#CA8A04" }}>
              {t("techPortal.chkUploading")}
            </span>
          ) : (
            <PhotoSourceButtons dark={dark} onPick={(src) => pick(nextKind, src)} />
          )}
        </div>
      )}
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {sequence
            .filter((k) => byKind.has(k))
            .map((kind) => (
              <button
                key={kind}
                type="button"
                title={t(SLOT_LABEL_KEYS[kind])}
                disabled={uploadingKind != null}
                onClick={() => {
                  if (issueMode == null) {
                    setChooser(kind);
                    return;
                  }
                  setOpenSlot((cur) => (cur === kind ? null : kind));
                  setIssueNote("");
                }}
                className="relative h-11 w-11 overflow-hidden rounded border"
                style={{
                  borderColor: issueByKind.get(kind) != null ? "#DC2626" : "#65A30D",
                  outline: openSlot === kind ? "2px solid #CA8A04" : undefined,
                }}
              >
                <img
                  src={byKind.get(kind)}
                  alt={t(SLOT_LABEL_KEYS[kind])}
                  className="h-full w-full object-cover"
                />
                {issueByKind.get(kind) != null && (
                  <span className="absolute bottom-0 right-0 bg-red-600 px-0.5 text-xs leading-none text-white">
                    ⚠
                  </span>
                )}
              </button>
            ))}
        </div>
      )}
      {/* F-montador-fases — peça com defeito (antes) / quebrada (fim). */}
      {issueMode != null && openSlot != null && (
        <div className="space-y-1 rounded-md border border-amber-500/50 bg-neutral-950 p-2">
          <p className="text-xs font-semibold text-amber-200">{t(SLOT_LABEL_KEYS[openSlot])}</p>
          {issueByKind.get(openSlot) == null ? (
            <>
              <input
                value={issueNote}
                onChange={(e) => setIssueNote(e.target.value)}
                placeholder={t("techPortal.chkIssueNotePlaceholder")}
                className="w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onSetIssue?.(openSlot, issueMode, issueNote);
                    setOpenSlot(null);
                  }}
                  className="rounded border border-red-500/60 px-2 py-1 text-xs text-red-300"
                >
                  ⚠{" "}
                  {issueMode === "defect"
                    ? t("techPortal.chkMarkDefect")
                    : t("techPortal.chkMarkBroken")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpenSlot(null);
                    setChooser(openSlot);
                  }}
                  className="rounded border border-neutral-600 px-2 py-1 text-xs text-neutral-300"
                >
                  📷 {t("techPortal.chkRetakePhoto")}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                onSetIssue?.(openSlot, null, "");
                setOpenSlot(null);
              }}
              className="rounded border border-neutral-600 px-2 py-1 text-xs text-neutral-300"
            >
              ✕ {t("techPortal.chkClearIssue")}
            </button>
          )}
        </div>
      )}
      {chooser != null && uploadingKind == null && (
        <PhotoSourceChooser
          kind={chooser}
          dark={dark}
          onPick={(src) => pick(chooser, src)}
          onCancel={() => setChooser(null)}
        />
      )}
      {issueMode != null && flagged.length > 0 && (
        <p className="text-xs text-red-300">
          ⚠ {flagged.length}{" "}
          {issueMode === "defect"
            ? t("techPortal.chkIssuesDefectSummary")
            : t("techPortal.chkIssuesBrokenSummary")}
        </p>
      )}
    </div>
  );
}
